import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, User, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { BlogPostSchema, BreadcrumbSchema } from '../components/Schema';
import SocialShare from '../components/SocialShare';
import PrintButton from '../components/PrintButton';
import BookmarkButton from '../components/BookmarkButton';
import CommentsSection from '../components/CommentsSection';
import LazyImage from '../components/LazyImage';
import BlogContent from '../components/BlogContent';
import BannerAd from '../components/BannerAd';
import { resolveImageUrl } from '../utils/imageUrl';
import { calculateReadingTime, formatReadingTime } from '../utils/readingTime';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch the main post
      const postResponse = await axios.get(`${API}/admin/blog/posts/${postId}`);
      setPost(postResponse.data);
      
      // Fetch related posts based on category
      const relatedResponse = await axios.get(`${API}/admin/blog/posts/related/${postId}`);
      setRelatedPosts(relatedResponse.data.posts || []);
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError('Failed to load the article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl text-white mb-4">{error || 'Article not found'}</h1>
        <Link to="/blog">
          <Button className="btn-primary">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  const postUrl = `${window.location.origin}/blog/${post.id}`;

  return (
    <div className="min-h-screen bg-gray-950">
      <SEO 
        title={`${post.title} | TruckAccidentSupport.com`}
        description={post.excerpt}
        canonical={`/blog/${post.id}`}
        keywords={`${post.category}, truck safety, trucking industry`}
        ogImage={post.image}
      />
      <BlogPostSchema post={post} />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' },
        { name: post.title, path: `/blog/${post.id}` }
      ]} />

      {/* Hero Section */}
      <section className="relative py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center text-orange-400 hover:text-orange-300 mb-6 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
            
            <div className="mb-4">
              <span className="badge badge-orange">{post.category}</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-6">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                {new Date(post.date).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </div>
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                {formatReadingTime(calculateReadingTime(post.content))}
              </div>
              {post.author && (
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  {post.author}
                </div>
              )}
            </div>

            {/* Share and Print */}
            <div className="flex items-center gap-4 pb-6 border-b border-gray-800">
              <SocialShare 
                url={postUrl}
                title={post.title}
                description={post.excerpt}
              />
              <PrintButton 
                title={post.title}
                content={post.content}
                image={post.image}
                excerpt={post.excerpt}
              />
              <BookmarkButton post={post} variant="button" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.image && (
        <section className="bg-gray-950">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto -mt-8">
              <img 
                src={resolveImageUrl(post.image)} 
                alt={post.title}
                className="w-full h-auto rounded-xl shadow-2xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800';
                }}
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {/* Article Content */}
      <section className="py-12 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Sidebar Ad for Desktop */}
            <div className="lg:flex lg:gap-8">
              <article className="lg:flex-1">
                <div 
                  className="prose prose-lg prose-invert max-w-none
                    prose-headings:text-white prose-headings:font-bold
                    prose-p:text-gray-300 prose-p:leading-relaxed
                    prose-a:text-orange-400 prose-a:no-underline hover:prose-a:text-orange-300
                    prose-strong:text-white
                    prose-ul:text-gray-300 prose-ol:text-gray-300
                    prose-li:marker:text-orange-500"
                >
                  {/* Render content with YouTube embed support */}
                  <BlogContent content={post.content} />
                </div>

                {/* Between Content Ad */}
                <BannerAd position="between_content" page="blog" className="my-8" />

                {/* Share Section at Bottom */}
                <div className="mt-12 pt-8 border-t border-gray-800">
                  <h3 className="text-lg font-semibold text-white mb-4">Share this article</h3>
                  <SocialShare 
                    url={postUrl}
                    title={post.title}
                    description={post.excerpt}
                  />
                </div>

                {/* Comments Section */}
                <div className="mt-12">
                  <CommentsSection postId={postId} />
                </div>
              </article>
              
              {/* Sidebar Ad - Desktop Only */}
              <aside className="hidden lg:block lg:w-72 lg:flex-shrink-0">
                <div className="sticky top-8">
                  <BannerAd position="sidebar" page="blog" />
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts Section */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
                Related Articles
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Card key={relatedPost.id} className="card-hover bg-gray-800 border-gray-700 overflow-hidden">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <span className="badge badge-orange text-xs">{relatedPost.category}</span>
                        <div className="flex items-center text-xs text-gray-400">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(relatedPost.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                      </div>
                      <CardTitle className="text-white text-lg line-clamp-2">
                        <Link 
                          to={`/blog/${relatedPost.id}`}
                          className="hover:text-orange-400 transition-colors"
                        >
                          {relatedPost.title}
                        </Link>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-400 text-sm line-clamp-2 mb-4">
                        {relatedPost.excerpt}
                      </CardDescription>
                      <Link to={`/blog/${relatedPost.id}`}>
                        <Button variant="link" className="text-orange-400 hover:text-orange-300 p-0">
                          Read Article
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Stay Updated on Truck Safety
            </h2>
            <p className="text-gray-400 mb-6">
              Subscribe to our newsletter for the latest safety tips, industry news, and expert advice.
            </p>
            <Link to="/#newsletter">
              <Button className="btn-primary">
                Subscribe to Newsletter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
